/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { View } from '@components/atoms/views/View'
import { BoSegments, BoSegmentItem } from './BoSegments'
import { useBoSizeClass } from './useBoSizeClass'
import { mono, boZ } from './backoffice.tokens'

type Mode = 'flat' | 'anchors' | 'tabs'

const Ctx = React.createContext<{ mode: Mode }>({ mode: 'flat' })

interface SectionProps {
	/** 같은 id 를 여러 번 쓰면 한 탭으로 묶인다(DOM 순서는 그대로) — 배송 자세히의 「정보」 = 큰 수 + 수취인 + 배송 */
	id: string
	/** 첫 등장에서만 읽는다 */
	label?: string
	count?: number
	children?: React.ReactNode
}

/**
 * 자세히 화면의 한 섹션. 데스크톱·폰에서는 DOM 을 더하지 않는다(Fragment).
 * 태블릿에서만 앵커 래퍼가 선다 — 칩을 누르면 여기로 스크롤한다.
 */
export const BoDetailSection = (props: SectionProps) => {
	const { mode } = React.useContext(Ctx)
	if (mode === 'anchors') {
		return (
			<View className="boDetailAnchor" data-detail-section={props.id}>
				{props.children}
			</View>
		)
	}
	return <>{props.children}</>
}

interface PropsType {
	children: React.ReactNode
	/** 쿼리 키. 기본 `tab` — `/main/order/:id?tab=items` (RESPONSIVE_PLAN §6.1) */
	param?: string
	className?: string
}

/**
 * 자세히 한 장 → 폭 등급별 구조 (RESPONSIVE_PLAN §6.1·§6.2).
 *   데스크톱 = 그대로 한 장(Fragment, DOM 불변)
 *   태블릿   = 한 스크롤러 + 상단 sticky 칩 줄(앵커) — 페이지를 안 나눈다
 *   폰       = 상단 탭 줄 + **활성 섹션만** 그린다. 탭은 URL 쿼리 `?tab=` 가 정본(replace — 뒤로가기는 한 단만 닫힌다)
 * 자식이 비어 있는 섹션(조건부 렌더 결과 false/null)은 탭에서 빠진다.
 * 페이지·자세히 컴포넌트는 등급을 모른다 — 여기가 유일한 분기다.
 */
export const BoDetailSections = (props: PropsType) => {
	const sizeClass = useBoSizeClass()
	const param = props.param ?? 'tab'
	const [searchParams, setSearchParams] = useSearchParams()
	const rootRef = React.useRef<HTMLDivElement | null>(null)
	const [anchor, setAnchor] = React.useState<string>('')

	const sections = React.Children.toArray(props.children).filter(
		React.isValidElement,
	) as React.ReactElement<SectionProps>[]

	const tabs: BoSegmentItem[] = []
	sections.forEach(el => {
		const { id, label, count, children } = el.props
		if (React.Children.toArray(children).length === 0) return
		const found = tabs.find(t => t.id === id)
		if (found !== undefined) {
			if (found.label === '' && label !== undefined) found.label = label
			return
		}
		tabs.push({ id, label: label ?? '', count })
	})

	const mode: Mode =
		tabs.length <= 1
			? 'flat'
			: sizeClass === 'phone'
			? 'tabs'
			: sizeClass === 'tablet'
			? 'anchors'
			: 'flat'
	const ctx = React.useMemo(() => ({ mode }), [mode])

	if (mode === 'flat') {
		return <Ctx.Provider value={ctx}>{sections}</Ctx.Provider>
	}

	if (mode === 'anchors') {
		const onChip = (id: string) => {
			setAnchor(id)
			const el = rootRef.current?.querySelector<HTMLElement>(
				`[data-detail-section="${id}"]`,
			)
			if (el && typeof el.scrollIntoView === 'function') {
				el.scrollIntoView({ block: 'start', behavior: 'smooth' })
			}
		}
		return (
			<Ctx.Provider value={ctx}>
				<View
					css={style}
					className={`boDetailSections anchors ${props.className ?? ''}`}
					ref={rootRef}
				>
					<BoSegments
						variant="chips"
						className="boDetailSegments"
						items={tabs}
						value={anchor !== '' ? anchor : tabs[0].id}
						onChange={onChip}
					/>
					{sections}
				</View>
			</Ctx.Provider>
		)
	}

	const raw = searchParams.get(param) ?? ''
	const active = tabs.some(t => t.id === raw) ? raw : tabs[0].id
	const onTab = (id: string) => {
		const next = new URLSearchParams(searchParams)
		if (id === tabs[0].id) next.delete(param)
		else next.set(param, id)
		setSearchParams(next, { replace: true })
	}
	return (
		<Ctx.Provider value={ctx}>
			<View css={style} className={`boDetailSections tabs ${props.className ?? ''}`}>
				<BoSegments
					variant="tabs"
					className="boDetailSegments"
					items={tabs}
					value={active}
					onChange={onTab}
				/>
				{sections.filter(el => el.props.id === active)}
			</View>
		</Ctx.Provider>
	)
}

const style = css({
	// 탭·칩 줄은 스크롤러(FullModalLayout .contentView) 맨 위에 붙는다
	'.boDetailSegments': {
		position: 'sticky',
		top: 0,
		zIndex: boZ.sticky,
		background: mono.bg,
	},
	'&.anchors .boDetailSegments': { padding: '8px 0', height: 48, minHeight: 48 },
	// 칩으로 스크롤할 때 sticky 칩 줄 아래로 들어오게
	'.boDetailAnchor': { scrollMarginTop: 56 },
})
